import React, { useCallback, useState } from "react";
import { DarwinAgentType, DarwinHeader } from "build-ts/common";
import { AgentInputProto, ChatContext, ChatRequest } from "build-ts/chat_session";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto } from "build-ts/schedule";
import { refreshSystemPrompt, updateChatSessionHeaders } from "services/AgentChatService";
import { getAnalyst } from "services/AnalystService";
import showToast from "hooks/useCustomToast";
import ChatPanelHeader from "../components/ChatPanelHeader";

interface ChatPanelHeaderProps {
  chatId: string;
  sessionHeader: DarwinHeader;
  setSessionHeader: (header: DarwinHeader) => void;
  onDelete: () => void;
  onAfterRename: () => void;
  onToggleArtifacts?: () => void;
  isArtifactsPanelVisible?: boolean;
  analystsList?: Header[];
  selectedAnalyst?: Header | null;
  scenarioJobs?: ScheduledJobProto[];
  isFromEva?: boolean;
  onViewPrompt?: () => void;
  isChatSaved?: boolean;
  hasChatMessages?: boolean;
  onSaveChat?: () => void;
}

const ChatPanelHeaderContainer: React.FC<ChatPanelHeaderProps> = props => {
  const { chatId, sessionHeader, setSessionHeader, onAfterRename, selectedAnalyst, scenarioJobs } = props;

  const [isRefreshingAnalyst, setIsRefreshingAnalyst] = useState(false);

  const handleRename = useCallback(
    async (id: string, newName: string) => {
      if (!newName.trim() || !sessionHeader) return;
      const success = await updateChatSessionHeaders([id], newName.trim(), undefined, false);
      if (success) {
        showToast("Chat renamed", "", "success");
        setSessionHeader({ ...sessionHeader, name: newName.trim() });
        window.dispatchEvent(
          new CustomEvent("chat:session-renamed", { detail: { sessionId: id, name: newName.trim() } })
        );
        onAfterRename();
      }
    },
    [sessionHeader, setSessionHeader, onAfterRename]
  );

  const handleDescSave = useCallback(
    async (descDraft: string, onSuccess: () => void) => {
      const success = await updateChatSessionHeaders([chatId], sessionHeader?.name, descDraft.trim(), false);
      if (success) {
        showToast("Description saved", "", "success");
        onSuccess();
      }
    },
    [chatId, sessionHeader?.name]
  );

  const handleRefreshAnalyst = useCallback(async () => {
    if (!selectedAnalyst?.id || !sessionHeader?.id) return;
    setIsRefreshingAnalyst(true);
    const result = await getAnalyst(selectedAnalyst.id);
    if (result) {
      const chatContext = ChatContext.create({
        agent_type: DarwinAgentType.ANALYST_CHAT_AGENT,
        agent_input: AgentInputProto.create({
          analyst: result,
          scenario_jobs: scenarioJobs ?? [],
        }),
      });
      const chatRequest: ChatRequest = ChatRequest.create({
        session_header: sessionHeader,
        chat_context: chatContext,
        is_save: true,
      });
      await refreshSystemPrompt(chatRequest);
    }
    setIsRefreshingAnalyst(false);
  }, [selectedAnalyst?.id, sessionHeader, scenarioJobs]);

  return (
    <ChatPanelHeader
      {...props}
      isRefreshingAnalyst={isRefreshingAnalyst}
      onRename={handleRename}
      onDescSave={handleDescSave}
      onRefreshAnalyst={handleRefreshAnalyst}
    />
  );
};

export default ChatPanelHeaderContainer;
